import {
  DEFAULT_BRAND,
  isClientTypeAllowedForBrand,
  type BrandKey,
  type ClientTypeValue,
} from "@school-sales-ai/shared";

/**
 * The pure parts of the client profile form: its shape, its validation and the
 * mapping to the API payload. Separate from `ClientFormFields.tsx` so that file
 * exports only a component.
 *
 * Shared by the Clients modal and step 1 of the onboarding wizard, so the two
 * cannot validate the same client differently.
 */

export type ClientFormValues = {
  name: string;
  slug: string;
  planId: string;
  originsText: string;
  clientType: ClientTypeValue | "";
  /**
   * The segment the client was stored with when it does not belong to its brand —
   * a Loxago shop filed as "Academia" before segments followed the brand. The form
   * starts with `clientType` empty and shows this so the operator knows what to
   * replace; it is never sent. Null once a segment is chosen.
   */
  mismatchedClientType: ClientTypeValue | null;
  // Never "" — unlike clientType there is no unbranded state for a client.
  brand: BrandKey;
  city: string;
  timezone: string;
};

export type ClientFormErrors = {
  name?: string;
  slug?: string;
  planId?: string;
  origins?: string;
  timezone?: string;
};

/** The pilot's zone. Every school onboarded so far is in Mexico. */
export const DEFAULT_TIMEZONE = "America/Mexico_City";

// A handful of Mexican zones plus the one the border cities use. A free-text IANA
// name is a support ticket waiting to happen, and the backend caps the column at 64
// chars without checking that it resolves.
export const TIMEZONE_OPTIONS = [
  "America/Mexico_City",
  "America/Monterrey",
  "America/Chihuahua",
  "America/Hermosillo",
  "America/Tijuana",
  "America/Cancun",
] as const;

export const EMPTY_CLIENT_FORM: ClientFormValues = {
  name: "",
  slug: "",
  planId: "",
  originsText: "",
  clientType: "",
  mismatchedClientType: null,
  brand: DEFAULT_BRAND.key,
  city: "",
  timezone: DEFAULT_TIMEZONE,
};

/**
 * The form after the operator picks a brand. Segments belong to a brand, so a segment
 * of the brand being left is cleared rather than carried to one that cannot hold it
 * (the API would refuse the pair).
 */
export function withBrand(values: ClientFormValues, brand: BrandKey): ClientFormValues {
  const keepType =
    values.clientType !== "" && isClientTypeAllowedForBrand(values.clientType, brand);
  return { ...values, brand, clientType: keepType ? values.clientType : "" };
}

/** name → slug suggestion: strip diacritics, lowercase, collapse to hyphens. */
export function slugify(name: string): string {
  return name
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/** Splits the textarea (one origin per line) into a trimmed, non-empty list. */
export function parseOrigins(text: string): string[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

// Client-side sanity check; the backend schema is authoritative (it normalizes to
// the exact origin string the CORS check compares against).
function findInvalidOrigin(origins: string[]): string | null {
  for (const o of origins) {
    try {
      const url = new URL(o);
      if (url.protocol !== "http:" && url.protocol !== "https:") return o;
    } catch {
      return o;
    }
  }
  return null;
}

export function validateClientForm(
  values: ClientFormValues,
  opts: { isEdit: boolean },
): ClientFormErrors {
  const errors: ClientFormErrors = {};
  if (values.name.trim().length < 2) {
    errors.name = "El nombre es obligatorio (mín. 2 caracteres)";
  }
  // The slug is immutable after creation, so it is only validated on create.
  if (!opts.isEdit && !/^[a-z0-9-]{2,64}$/.test(values.slug)) {
    errors.slug = "Slug inválido (solo minúsculas, números y guiones)";
  }
  if (!values.planId) errors.planId = "Debes seleccionar un plan";
  const invalid = findInvalidOrigin(parseOrigins(values.originsText));
  if (invalid) errors.origins = `Origen inválido: ${invalid} (usa http(s)://dominio)`;
  if (!values.timezone.trim()) errors.timezone = "La zona horaria es obligatoria";
  return errors;
}

/**
 * Maps the form to the API payload.
 *
 * `clientType` and `city` are optional, so an empty selection must send `null`
 * rather than `""` — the backend's `city` schema has a `min(1)`, and a blank string
 * would come back as a validation error on a field the operator left alone on
 * purpose.
 */
export function toClientProfileInput(values: ClientFormValues): {
  name: string;
  planId: string | null;
  widgetOrigins: string[];
  clientType: ClientTypeValue | null;
  brand: BrandKey;
  city: string | null;
  timezone: string | null;
} {
  return {
    name: values.name.trim(),
    planId: values.planId || null,
    widgetOrigins: parseOrigins(values.originsText),
    clientType: values.clientType === "" ? null : values.clientType,
    brand: values.brand,
    city: values.city.trim() || null,
    timezone: values.timezone.trim() || null,
  };
}

/** Builds the form state from an existing client (edit mode / wizard resume). */
export function clientToFormValues(client: {
  name: string;
  slug: string;
  plan: { id: string } | null;
  widgetOrigins: string[];
  clientType: ClientTypeValue | null;
  brand?: BrandKey;
  city: string | null;
  timezone: string | null;
}): ClientFormValues {
  // Optional on the way in because the CRM can briefly run against a backend
  // that does not return it yet — Vercel and Render deploy independently.
  const brand = client.brand ?? DEFAULT_BRAND.key;
  // A segment of the other brand cannot be saved back (the API refuses the pair),
  // so it is not preselected — only reported.
  const mismatched =
    client.clientType !== null && !isClientTypeAllowedForBrand(client.clientType, brand);
  return {
    name: client.name,
    slug: client.slug,
    planId: client.plan?.id ?? "",
    originsText: client.widgetOrigins.join("\n"),
    clientType: mismatched ? "" : (client.clientType ?? ""),
    mismatchedClientType: mismatched ? client.clientType : null,
    brand,
    city: client.city ?? "",
    timezone: client.timezone ?? DEFAULT_TIMEZONE,
  };
}
